import { cn } from "@/lib/utils";

export type TablerIconName =
  | "bolt"
  | "file-text"
  | "star"
  | "home"
  | "files"
  | "file-contract"
  | "calendar"
  | "coins"
  | "settings"
  | "plus"
  | "search"
  | "lock"
  | "map-pin"
  | "clock"
  | "trash"
  | "chevron-down"
  | "bell"
  | "building"
  | "receipt"
  | "user"
  | "menu-2"
  | "sun"
  | "moon";

type TablerIconProps = {
  name: TablerIconName;
  className?: string;
  size?: number;
};

/** Ikony Tabler (stroke) — spójne z kreatorem Workshop Modern. */
export function TablerIcon({ name, className, size = 16 }: TablerIconProps) {
  const common = {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: cn("shrink-0", className),
    "aria-hidden": true,
  };

  switch (name) {
    case "home":
      return (
        <svg {...common}>
          <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
          <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
          <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
        </svg>
      );
    case "files":
      return (
        <svg {...common}>
          <path d="M15 3v4a1 1 0 0 0 1 1h4" />
          <path d="M18 17h-14a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
          <path d="M16 17v2a2 2 0 0 0 2 2h2" />
          <path d="M10 12l0 6" />
        </svg>
      );
    case "file-contract":
      return (
        <svg {...common}>
          <path d="M8 21h-2a3 3 0 0 1 -3 -3v-12a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v6" />
          <path d="M18 22l3 -3l-3 -3" />
          <path d="M11 18h7" />
          <path d="M9 12h6" />
          <path d="M9 16h3" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...common}>
          <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" />
          <path d="M16 3v4" />
          <path d="M8 3v4" />
          <path d="M4 11h16" />
        </svg>
      );
    case "coins":
      return (
        <svg {...common}>
          <path d="M9 14c0 1.657 2.686 3 6 3s6 -1.343 6 -3s-2.686 -3 -6 -3s-6 1.343 -6 3z" />
          <path d="M9 14v1c0 1.657 2.686 3 6 3s6 -1.343 6 -3v-1" />
          <path d="M3 6c0 1.657 2.686 3 6 3s6 -1.343 6 -3s-2.686 -3 -6 -3s-6 1.343 -6 3z" />
          <path d="M3 6v1c0 1.657 2.686 3 6 3s6 -1.343 6 -3v-1" />
          <path d="M3 10c0 1.657 2.686 3 6 3s6 -1.343 6 -3s-2.686 -3 -6 -3s-6 1.343 -6 3z" />
          <path d="M3 10v1c0 1.657 2.686 3 6 3s6 -1.343 6 -3v-1" />
        </svg>
      );
    case "settings":
      return (
        <svg {...common}>
          <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
          <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
        </svg>
      );
    case "plus":
      return (
        <svg {...common}>
          <path d="M12 5l0 14" />
          <path d="M5 12l14 0" />
        </svg>
      );
    case "search":
      return (
        <svg {...common}>
          <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
          <path d="M21 21l-6 -6" />
        </svg>
      );
    case "lock":
      return (
        <svg {...common}>
          <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6z" />
          <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
          <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
        </svg>
      );
    case "map-pin":
      return (
        <svg {...common}>
          <path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
          <path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />
        </svg>
      );
    case "clock":
      return (
        <svg {...common}>
          <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
          <path d="M12 7v5l3 3" />
        </svg>
      );
    case "trash":
      return (
        <svg {...common}>
          <path d="M4 7l16 0" />
          <path d="M10 11l0 6" />
          <path d="M14 11l0 6" />
          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
          <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
        </svg>
      );
    case "chevron-down":
      return (
        <svg {...common}>
          <path d="M6 9l6 6l6 -6" />
        </svg>
      );
    case "bell":
      return (
        <svg {...common}>
          <path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
          <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
        </svg>
      );
    case "building":
      return (
        <svg {...common}>
          <path d="M3 21h18" />
          <path d="M9 8h1" />
          <path d="M9 12h1" />
          <path d="M9 16h1" />
          <path d="M14 8h1" />
          <path d="M14 12h1" />
          <path d="M14 16h1" />
          <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16" />
        </svg>
      );
    case "receipt":
      return (
        <svg {...common}>
          <path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2" />
          <path d="M9 14l6 0" />
          <path d="M9 10l6 0" />
        </svg>
      );
    case "user":
      return (
        <svg {...common}>
          <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
          <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
        </svg>
      );
    case "menu-2":
      return (
        <svg {...common}>
          <path d="M4 6l16 0" />
          <path d="M4 12l16 0" />
          <path d="M4 18l16 0" />
        </svg>
      );
    case "sun":
      return (
        <svg {...common}>
          <path d="M12 12m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
          <path d="M3 12h1m8 -9v1m8 8h1m-9 8v1m-6.4 -15.4l.7 .7m12.1 -.7l-.7 .7m0 11.4l.7 .7m-12.1 -.7l-.7 .7" />
        </svg>
      );
    case "moon":
      return (
        <svg {...common}>
          <path d="M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 8.4 8.4a9 9 0 1 1 -8.7 -8.4z" />
        </svg>
      );
    case "bolt":
      return (
        <svg {...common}>
          <path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11" />
        </svg>
      );
    case "file-text":
      return (
        <svg {...common}>
          <path d="M14 3v4a1 1 0 0 0 1 1h4" />
          <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
          <path d="M9 9l1 0" />
          <path d="M9 13l6 0" />
          <path d="M9 17l6 0" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" />
        </svg>
      );
  }
}
